import type { TFormatMessage } from '@zennolab_platform/webui-sdk/dist/hooks/useFormatMessage';

import type { IAnnouncementAudience } from '../types/announcement';

/** Form state of the audience block; `targeted: false` is "Все" and sends no audience. */
export interface IAudienceDraft {
  targeted: boolean;
  tariffs: string[];
  languages: string[];
  /** As typed; trimmed when sent, blank = no bound. */
  versionMin: string;
  versionMax: string;
}

/** The audience to send; undefined for "Все" or no condition (the service reads both as everyone). */
export function toRequestAudience(d: IAudienceDraft): IAnnouncementAudience | undefined {
  if (!d.targeted) return undefined;
  const audience: IAnnouncementAudience = {
    tariffs: d.tariffs,
    languages: d.languages,
    versionMin: d.versionMin.trim() || undefined,
    versionMax: d.versionMax.trim() || undefined,
  };
  const empty = !audience.tariffs.length && !audience.languages.length && !audience.versionMin && !audience.versionMax;
  return empty ? undefined : audience;
}

export function toAudienceDraft(a: IAnnouncementAudience | undefined): IAudienceDraft {
  const draft: IAudienceDraft = {
    targeted: true,
    tariffs: a?.tariffs ?? [],
    languages: a?.languages ?? [],
    versionMin: a?.versionMin ?? '',
    versionMax: a?.versionMax ?? '',
  };
  return { ...draft, targeted: toRequestAudience(draft) !== undefined };
}

/** The conditions in words joined with "и" ("Тариф Pro и язык ru и версия от 1.22"); '' means everyone. */
export function describeAudience(t: TFormatMessage, a: IAnnouncementAudience | undefined): string {
  if (!a) return '';
  const parts: string[] = [];
  if (a.tariffs.length) {
    parts.push(
      t(
        {
          id: 'announcements.audience.tariffPart',
          defaultMessage: '{count, plural, one {тариф} other {тарифы}} {list}',
          description: 'Часть описания аудитории; count — число тарифов, list — их коды',
        },
        { count: a.tariffs.length, list: a.tariffs.join(', ') },
      ),
    );
  }
  if (a.languages.length) {
    parts.push(
      t(
        {
          id: 'announcements.audience.languagePart',
          defaultMessage: '{count, plural, one {язык} other {языки}} {list}',
          description: 'Часть описания аудитории; count — число языков, list — их коды',
        },
        { count: a.languages.length, list: a.languages.join(', ') },
      ),
    );
  }
  if (a.versionMin && a.versionMax) {
    parts.push(
      t(
        {
          id: 'announcements.audience.versionRange',
          defaultMessage: 'версия от {min} до {max}',
          description: 'Часть описания аудитории: диапазон версий',
        },
        { min: a.versionMin, max: a.versionMax },
      ),
    );
  } else if (a.versionMin) {
    parts.push(
      t(
        {
          id: 'announcements.audience.versionFrom',
          defaultMessage: 'версия от {min}',
          description: 'Часть описания аудитории: нижняя граница версии',
        },
        { min: a.versionMin },
      ),
    );
  } else if (a.versionMax) {
    parts.push(
      t(
        {
          id: 'announcements.audience.versionTo',
          defaultMessage: 'версия до {max}',
          description: 'Часть описания аудитории: верхняя граница версии',
        },
        { max: a.versionMax },
      ),
    );
  }
  const and = t({
    id: 'announcements.audience.and',
    defaultMessage: 'и',
    description: 'Союз между частями описания аудитории',
  });
  const text = parts.join(` ${and} `);
  return text.charAt(0).toUpperCase() + text.slice(1);
}
